"use client"

import { useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import Image from "next/image"
import Link from "next/link"
import { Star, Heart, ShoppingCart, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { fetchFeaturedProducts, getImageUrl, calculateDiscountedPrice, isDiscountActive } from "@/lib/utils"
import { useCart } from "@/hooks/use-cart"
import DiscountBadge from "@/components/discount-badge"

export default function FeaturedProducts() {
  const router = useRouter()
  const [products, setProducts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { addItem, state } = useCart()

  useEffect(() => {
    setLoading(true)
    setError(null)
    fetchFeaturedProducts()
      .then((data) => {
        // Map API fields to frontend fields
        const mappedProducts = (data.data || data || []).map((p: any) => {
          const originalPrice = Number(p.selling_price || p.price || 0)
          const { discountedPrice, discountAmount } = calculateDiscountedPrice(originalPrice)
          const isDiscountApplied = isDiscountActive() && discountAmount > 0
          
          return {
            id: p.id,
            name: p.product_name || p.name || p.code || 'Product',
            price: isDiscountApplied ? discountedPrice : originalPrice,
            originalPrice: isDiscountApplied ? originalPrice : (p.compare_price ? Number(p.compare_price) : undefined),
            rating: p.rating ?? 0,
            reviews: p.reviews ?? 0,
            image: getImageUrl(p.image || (p.images && p.images.length > 0 ? p.images[0].image : undefined)),
            images: p.images ? p.images.map((img: any) => getImageUrl(img.image)).filter(Boolean) : [],
            category: p.categ || p.category || '',
            brand: p.brand || '',
            condition: p.condition || '',
            inStock: p.in_stock ?? (p.stock_available > 0) ?? true,
            stockAvailable: p.stock_available ?? 0,
            discount: p.discount ?? 0,
            description: p.description || p.short_description || '',
            packSize: p.pack_size || '',
            barcode: p.barcode || '',
            prescriptionOnly: p.prescription_only || false,
            isVatable: p.is_vatable || false,
            isDiscounted: p.is_discounted || false,
            ...p,
          }
        })
        setProducts(mappedProducts)
        setLoading(false)
      })
      .catch((err) => {
        setError("Failed to load featured products.")
        setLoading(false)
      })
  }, [])

  const handleAddToCart = (product: any) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice,
      image: product.image,
      inStock: product.inStock,
      is_vatable: product.isVatable,
    })
  }

  const handleProductClick = (product: any) => {
    router.push(`/product/${product.id}`)
  }

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center text-lg text-gray-500">Loading featured products...</div>
      </section>
    )
  }
  if (error) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center text-red-500">{error}</div>
      </section>
    )
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12 gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Featured Products</h2>
            <p className="text-gray-600 text-sm sm:text-base">Top-rated products trusted by thousands of customers</p>
          </div>
          <Button variant="outline" asChild className="w-full sm:w-auto">
            <Link href="/shop">View All Products</Link>
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {products.map((product) => (
            <Card key={product.id} className="group hover:shadow-lg transition-all duration-300">
              <CardContent className="p-0">
                <div className="relative">
                  <Image
                    src={product.image || "/banners/no-img.jpg"}
                    alt={product.name}
                    width={300}
                    height={300}
                    className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer"
                    onClick={() => handleProductClick(product)}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/banners/no-img.jpg";
                    }}
                  />
                  <DiscountBadge />
                  {!product.inStock && (
                    <Badge variant="secondary" className="absolute top-2 right-2 text-xs">
                      Out of Stock
                    </Badge>
                  )}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="sm" variant="outline" className="w-8 h-8 p-0 bg-white">
                      <Heart className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="p-3 sm:p-4">
                  <Badge variant="outline" className="text-xs mb-2">
                    {product.category}
                  </Badge>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2">{product.name}</h3>

                  {/* <div className="flex items-center gap-1 mb-2">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < Math.floor(product.rating) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-500">({product.reviews})</span>
                  </div>
 */}
                  <div className="flex items-center gap-2 mb-3 sm:mb-4">
                    <span className="text-base sm:text-lg font-bold text-green-600">KSh {product.price.toLocaleString()}</span>
                    {product.originalPrice && (
                      <span className="text-xs sm:text-sm text-gray-500 line-through">
                        {product.originalPrice > product.price && (
                          <span>
                            KSh {product.originalPrice.toLocaleString()}
                          </span>
                        )}
                      </span>
                    )}
                  </div>

                  <Button
                    className="w-full text-xs sm:text-sm"
                    disabled={!product.inStock || state.isLoading}
                    variant={product.inStock ? "default" : "secondary"}
                    onClick={() => handleAddToCart(product)}
                  >
                    {state.isLoading ? (
                      <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                    ) : (
                      <ShoppingCart className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    )}
                    {state.isLoading ? "Adding..." : (product.inStock ? "Add to Cart" : "Out of Stock")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  )
}
